import { Component, OnInit, Input, OnDestroy, ElementRef, AfterViewInit } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import get from 'lodash-es/get';

import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { ClipPathTypes } from '@finder/shared/directives/clip-path/clip-path.interface';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { CommonModule } from '@angular/common';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { LinkIdDirective } from '@finder/shared/directives/link-id/link-id.directive';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { PictureComponent } from '../picture/picture.component';
import { AnimationComponent } from '../animations/animation/animation.component';
import { ClipPathDirective } from '@finder/shared/directives/clip-path/clip-path.directive';
import { InViewportDirective } from '@finder/shared/directives/in-viewport/in-viewport.directive';
import { TrustResourceUrlPipe } from '@finder/shared/utils/pipes/trust-resource-url/trust-resource-url.pipe';
import { IsTitlePipe } from './is-title.pipe';
import { PlayerBlockComponent } from '../player-block/player-block.component';
import { LocaleDirective } from '@finder/shared/directives/locale/locale.directive';
import { RouterLinkDirective } from '@finder/shared/directives/router-link/router-link.directive';

@Component({
    selector: 'finder-image-block',
    templateUrl: './image-block.component.html',
    styleUrls: ['./image-block.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ComponentHeaderComponent,
        LinkIdDirective,
        SafeHtmlPipe,
        FontIconComponent,
        PictureComponent,
        AnimationComponent,
        ClipPathDirective, // finderClipPath
        InViewportDirective, // finderInViewport
        LocaleDirective, // finderLocale
        LinkIdDirective, // finderLinkId
        RouterLinkDirective, // finderRouterLink
        IsTitlePipe,
        PlayerBlockComponent,
    ]
})
export class ImageBlockComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() blockData;

    hasBlockItems = false;
    requireFullElement = true;
    addedOffset = 0;
    isVisible = true;
    styleClass: string;

    private destroy$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private finderAnalyticsService: FinderAnalyticsService,
        private elementRef: ElementRef,
        private viewport: ViewportDetectionService,
    ) {}

    ngOnInit() {
        const animations = get(this.blockData, 'animations');
        const items = get(this.blockData, 'items', []);
        this.hasBlockItems = items.length > 0;
        this.blockData.items = this.addAnimation(animations, items);
        this.updateCinemagraphs();
        this.setVisibility();
        this.styleClass = get(this.blockData, 'style', '');
        if (this.viewport.isMobile()) {
            this.requireFullElement = false;
        }
    }

    ngAfterViewInit() {
        this.addGradient();
    }

    // Ignore the if just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    addAnimation(animations: Array<any>, list: Array<any>) {
        const items = [...list]; // clone data to avoid side effects outside this method
        if (Array.isArray(animations)) {
            items
                .filter(item => item.type !== 'heading')
                .forEach((item, i) => {
                    const itemAnimation = animations.filter(animation =>
                        parseInt(animation.location, 10) === (i + 1)
                    );
                    if (itemAnimation.length > 0) {
                        item.animation = itemAnimation.pop();
                    }
                });
        }

        return items;
    }

    addGradient() {
        // catch elements with the gradient defined and loop over them
        this.elementRef.nativeElement.querySelectorAll('[gradient-overlay]').forEach((e: any) => {
            // apply class to add styling
            e.classList.add('gradient-overlay');
            // create css var based on attr value
            e.style.setProperty('--image-block-gradient-overlay-bg', e.getAttribute('gradient-overlay'));
            // clean up element
            e.removeAttribute('gradient-overlay');
        });
    }

    restartVideo($event) {
        const $cinemagraphWrapper = $event.srcElement;
        const cinemagraph = $cinemagraphWrapper.querySelector('.cinemagraph');

        if (!cinemagraph) {
            // when we do a focus in the 'a' element we also need to
            // run fadeCinemagraph but if the 'a' elment only has an image
            // we should just return.
            return;
        }

        const cinemagraphIFrame = cinemagraph.querySelector('iframe');

        try {
            // Currently there is no method to restart a cinemagraph video type in pixie player
            // so accessing the video directly to restart
            cinemagraphIFrame.contentWindow.ME.state.currentMediaEl.querySelector('video').currentTime = 0;
        } catch (err) {
            this.logger.error('Error pausing/resetting pixie player:', err);
        }
    }

    analyticsFireTrackSeen(linkId) {
        if (linkId) {
            this.finderAnalyticsService.updateModelAndTrack({linkId});
        }
    }

    getClipPath(blockData, item, index) {
        return {
            blockData,
            item,
            index,
            border: '3px',
            type: ClipPathTypes.imageBlock,
        };
    }

    inViewport(event) {
        if (this.isVisible === true) {
            return;
        }

        this.isVisible = event;
    }

    private updateCinemagraphs() {
        this.elementRef.nativeElement.querySelectorAll('.cinemagraph:not([autoplay])')
            .forEach((video: HTMLVideoElement) => {
                fromEvent(video, 'mouseover')
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                        video.play();
                    });
                fromEvent(video, 'mouseout')
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                        video.pause();
                        video.currentTime = 0;
                    });
            });
    }

    private setVisibility() {
        // If we have viewport triggering enabled then we want the visibility
        // of the block to be `false` so we can fade it in when the block
        // comes into the viewport
        const viewportTrigger = get(this.blockData, 'viewportTrigger', false);

        // Check viewport to override visible setting (if applicable)
        // This only applies to mobile - if on mobile we don't want to have to wait
        // until all elements are in the viewport because they are stacked
        this.isVisible =  viewportTrigger ? false : true;
    }
}
